/**
 * callFavourites slice — client-side, per-device list of favourite call peers.
 *
 * A favourite is a peer user the user wants one-tap quick-call access to.
 * Mirrors convPrefs: persisted to localStorage, no backend round-trip.
 * Ordering is newest-favourited first; re-adding an existing user is a no-op
 * (does not reorder or overwrite cached display fields).
 */
import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';

const STORAGE_KEY = 'gc.callFavourites.v1';

export interface Favourite {
  userId: string;
  name: string;
  avatarUrl: string | null;
}

export interface CallFavouritesState {
  items: Favourite[];
}

const defaults: CallFavouritesState = { items: [] };

function loadInitial(): CallFavouritesState {
  if (typeof window === 'undefined') return defaults;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw);
    const items: Favourite[] = Array.isArray(parsed.items)
      ? parsed.items
          .filter((x: any) => x && typeof x.userId === 'string')
          .map((x: any) => ({
            userId: x.userId as string,
            name: typeof x.name === 'string' ? x.name : 'Unknown',
            avatarUrl: typeof x.avatarUrl === 'string' ? x.avatarUrl : null,
          }))
      : [];
    return { items };
  } catch {
    return defaults;
  }
}

function persist(state: CallFavouritesState): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* quota / private mode — ignore */
  }
}

const slice = createSlice({
  name: 'callFavourites',
  initialState: loadInitial(),
  reducers: {
    addFavourite(state, action: PayloadAction<Favourite>) {
      const fav = action.payload;
      if (state.items.some((f) => f.userId === fav.userId)) return;
      state.items.unshift(fav);
      persist(state);
    },
    removeFavourite(state, action: PayloadAction<string>) {
      state.items = state.items.filter((f) => f.userId !== action.payload);
      persist(state);
    },
    toggleFavourite(state, action: PayloadAction<Favourite>) {
      const fav = action.payload;
      if (state.items.some((f) => f.userId === fav.userId)) {
        state.items = state.items.filter((f) => f.userId !== fav.userId);
      } else {
        state.items.unshift(fav);
      }
      persist(state);
    },
    refreshMeta(state, action: PayloadAction<Favourite>) {
      const { userId, name, avatarUrl } = action.payload;
      const f = state.items.find((x) => x.userId === userId);
      if (!f) return; // not a favourite — no-op (keeps reference stable)
      if (f.name === name && f.avatarUrl === avatarUrl) return; // unchanged — no write
      f.name = name;
      f.avatarUrl = avatarUrl;
      persist(state);
    },
  },
});

export const { addFavourite, removeFavourite, toggleFavourite, refreshMeta } = slice.actions;

export default slice.reducer;

type RootShape = { callFavourites: CallFavouritesState };

export const selectFavourites = (s: RootShape): Favourite[] => s.callFavourites.items;

export const selectFavouriteIds = createSelector(
  selectFavourites,
  (items): Set<string> => new Set(items.map((f) => f.userId)),
);
